import { motion } from "framer-motion";
import CountUp from "react-countup";
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area, ResponsiveContainer
} from "recharts";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { InfoTooltip, Skeleton, COLORS_DARK, COLORS_LIGHT } from "./DashboardShared";

export function KPICard({
  title, value, formattedValue, trend, trendLabel,
  icon: Icon, sparklineData, chartType = "line", delay = 0,
  primaryColor, tooltipText, isLoading = false, isDark = true
}) {
  const C = isDark ? COLORS_DARK : COLORS_LIGHT;
  const isPositive = trend > 0;
  const isNegative = trend < 0;
  const trendColor = isPositive ? C.positive : isNegative ? C.negative : C.neutral;
  const hasData = value > 0;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: delay / 1000, ease: "easeOut" }}>
      <Card className="transition-all duration-200 border"
        data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
        style={{ borderColor: isDark ? '#2A2A2A' : '#E5E7EB', cursor: 'default' }}>
        <CardContent className="p-3 md:p-5">
          <div className="flex items-start justify-between mb-2 md:mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${primaryColor}15` }}>
                <Icon className="w-4 h-4 md:w-5 md:h-5" style={{ color: primaryColor }} />
              </div>
              <span className="text-xs md:text-sm text-muted-foreground font-medium">{title}</span>
              <span className="hidden md:inline-block">{tooltipText && <InfoTooltip text={tooltipText} />}</span>
            </div>
          </div>
          <div className="mb-2 md:mb-3">
            {isLoading ? <Skeleton height="36px" width="120px" /> : (
              <p className="text-xl md:text-3xl font-bold tracking-tight">
                <CountUp end={value} duration={1.2} separator=" "
                  decimals={formattedValue?.includes(',') ? 2 : 0}
                  formattingFn={(val) => formattedValue || val.toLocaleString('fr-FR')} />
              </p>
            )}
          </div>
          <div className="flex items-center justify-between">
            {isLoading ? <Skeleton height="16px" width="100px" /> : (
              <div className="flex items-center gap-1 md:gap-1.5">
                {isPositive && <ArrowUpRight className="w-3.5 h-3.5 md:w-4 md:h-4" style={{ color: trendColor }} />}
                {isNegative && <ArrowDownRight className="w-3.5 h-3.5 md:w-4 md:h-4" style={{ color: trendColor }} />}
                {!isPositive && !isNegative && <Minus className="w-3.5 h-3.5 md:w-4 md:h-4" style={{ color: trendColor }} />}
                <span className="text-xs md:text-sm font-medium" style={{ color: trendColor }}>
                  {isPositive ? '+' : ''}{trend}%
                </span>
                <span className="hidden md:inline text-xs text-muted-foreground">{trendLabel}</span>
              </div>
            )}
          </div>
          {/* Sparkline - hidden on mobile */}
          <div className="hidden md:block">
            {isLoading ? (
              <div className="mt-3 h-12"><Skeleton height="48px" /></div>
            ) : sparklineData && sparklineData.length > 0 && hasData ? (
              <div className="mt-3 h-12">
                <ResponsiveContainer width="100%" height="100%">
                  {chartType === "bar" ? (
                    <BarChart data={sparklineData}><Bar dataKey="value" fill={primaryColor} radius={[2, 2, 0, 0]} /></BarChart>
                  ) : chartType === "area" ? (
                    <AreaChart data={sparklineData}>
                      <defs>
                        <linearGradient id={`gradient-${title}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={primaryColor} stopOpacity={0.3} />
                          <stop offset="100%" stopColor={primaryColor} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Area type="monotone" dataKey="value" stroke={primaryColor} strokeWidth={2} fill={`url(#gradient-${title})`} />
                    </AreaChart>
                  ) : (
                    <LineChart data={sparklineData}><Line type="monotone" dataKey="value" stroke={primaryColor} strokeWidth={2} dot={false} /></LineChart>
                  )}
                </ResponsiveContainer>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
